import type { JdxPayloadSchema } from "./types";

export const jdxBookingPayloadSchema: JdxPayloadSchema = {
  key: "shipment-booking",
  label: "JDX Shipment Booking",
  description:
    "Payload voor het aanmaken van een shipment booking richting de JDX booking API.",
  fields: [
    {
      path: "branchId",
      label: "Vestiging",
      group: "Configuratie",
      type: "string",
      required: true,
      sourceMode: "automatic",
      description: "Wordt automatisch gevuld vanuit Foundry.",
      example: "faa6cf9a-a3c5-4c6f-803d-fa7bebaf7048",
    },
    {
      path: "customer.id",
      label: "Klant",
      group: "Configuratie",
      type: "string",
      required: true,
      sourceMode: "automatic",
      description: "Wordt automatisch gevuld vanuit de geselecteerde klant.",
      example: "d0dd91d7-e648-4f21-86f3-ef37cf7b04a8",
    },
    {
      path: "customerReference",
      label: "Klantreferentie",
      group: "Basisgegevens",
      type: "string",
      required: true,
      description: "Referentie van de klant, zoals ordernummer of boekingsnummer.",
      example: "CR000001",
    },
    {
      path: "modality",
      label: "Transportsoort",
      group: "Basisgegevens",
      type: "enum",
      required: true,
      enumValues: ["OCEAN", "AIR", "ROAD", "RAIL"],
      example: "OCEAN",
    },
    {
      path: "loadType",
      label: "Ladingtype",
      group: "Basisgegevens",
      type: "enum",
      required: true,
      enumValues: ["FCL", "LCL", "FTL", "LTL", "ULD", "LOOSE"],
      example: "FCL",
    },
    {
      path: "movementType",
      label: "Richting",
      group: "Basisgegevens",
      type: "enum",
      required: true,
      enumValues: ["EXPORT", "IMPORT", "CROSSTRADE"],
      example: "EXPORT",
    },
    {
      path: "cargoReadyDate",
      label: "Laaddatum",
      group: "Planning",
      type: "date",
      required: true,
      example: "2024-08-01T14:15:00",
    },
    {
      path: "targetedArrivalDate",
      label: "Gewenste aankomstdatum",
      group: "Planning",
      type: "date",
      required: true,
      example: "2024-08-20T14:15:00",
    },
    {
      path: "originPort.unLoCode",
      label: "Vertrekhaven",
      group: "Route",
      type: "string",
      required: true,
      description: "UN/LOCODE van de vertrekhaven.",
      example: "NLAMS",
    },
    {
      path: "destinationPort.unLoCode",
      label: "Aankomsthaven",
      group: "Route",
      type: "string",
      required: true,
      description: "UN/LOCODE van de aankomsthaven.",
      example: "ITBCB",
    },
    {
      path: "incoterm",
      label: "Incoterm",
      group: "Route",
      type: "enum",
      required: true,
      enumValues: [
        "EXW",
        "FCA",
        "FOB",
        "CFR",
        "CPT",
        "CIF",
        "DPU",
        "DAP",
        "DDP",
        "OTHER",
      ],
      example: "EXW",
    },
    {
      path: "agent.id",
      label: "Agent",
      group: "Partijen",
      type: "string",
      example: "23cf1283-ce34-400e-a62f-6e1c54328e19",
    },
    {
      path: "agentReference",
      label: "Agentreferentie",
      group: "Partijen",
      type: "string",
      example: "AR000001",
    },
    {
      path: "shipperAddress",
      label: "Shipper adres",
      group: "Partijen",
      type: "object",
      children: [
        { path: "shipperAddress.name", label: "Naam", group: "Partijen", type: "string" },
        { path: "shipperAddress.streetAddress", label: "Straat", group: "Partijen", type: "string" },
        { path: "shipperAddress.city", label: "Plaats", group: "Partijen", type: "string" },
        { path: "shipperAddress.country", label: "Land", group: "Partijen", type: "string" },
        { path: "shipperAddress.postalCode", label: "Postcode", group: "Partijen", type: "string" },
        { path: "shipperAddress.email", label: "E-mail", group: "Partijen", type: "string" },
      ],
    },
    {
      path: "consigneeAddress",
      label: "Consignee adres",
      group: "Partijen",
      type: "object",
      children: [
        { path: "consigneeAddress.name", label: "Naam", group: "Partijen", type: "string" },
        { path: "consigneeAddress.streetAddress", label: "Straat", group: "Partijen", type: "string" },
        { path: "consigneeAddress.city", label: "Plaats", group: "Partijen", type: "string" },
        { path: "consigneeAddress.country", label: "Land", group: "Partijen", type: "string" },
        { path: "consigneeAddress.postalCode", label: "Postcode", group: "Partijen", type: "string" },
        { path: "consigneeAddress.email", label: "E-mail", group: "Partijen", type: "string" },
      ],
    },
    {
      path: "originOptions.hasDeclaration",
      label: "Origin declaration",
      group: "Opties",
      type: "boolean",
      example: true,
    },
    {
      path: "originOptions.hasCarriage",
      label: "Origin carriage",
      group: "Opties",
      type: "boolean",
      example: true,
    },
    {
      path: "destinationOptions.hasDeclaration",
      label: "Destination declaration",
      group: "Opties",
      type: "boolean",
      example: true,
    },
    {
      path: "destinationOptions.hasCarriage",
      label: "Destination carriage",
      group: "Opties",
      type: "boolean",
      example: true,
    },
    {
      path: "pickupAddress",
      label: "Pickup adres",
      group: "Pickup & delivery",
      type: "object",
    },
    {
      path: "pickupTransportMode",
      label: "Pickup transportmode",
      group: "Pickup & delivery",
      type: "enum",
      enumValues: ["TRUCK", "RAIL", "BARGE"],
      example: "TRUCK",
    },
    {
      path: "deliveryAddress",
      label: "Delivery adres",
      group: "Pickup & delivery",
      type: "object",
    },
    {
      path: "deliveryTransportMode",
      label: "Delivery transportmode",
      group: "Pickup & delivery",
      type: "enum",
      enumValues: ["TRUCK", "RAIL", "BARGE"],
      example: "TRUCK",
    },
    {
      path: "remarks",
      label: "Opmerkingen",
      group: "Overig",
      type: "string",
      example: "Additional booking notes",
    },
    {
      path: "containers",
      label: "Containers",
      group: "Containers",
      type: "array",
      children: [
        {
          path: "containers[].containerType.isoCode",
          label: "Container type",
          group: "Containers",
          type: "string",
          example: "22G0",
        },
        {
          path: "containers[].containerType.carrierOwned",
          label: "Carrier owned",
          group: "Containers",
          type: "boolean",
          example: false,
        },
        {
          path: "containers[].number",
          label: "Containernummer",
          group: "Containers",
          type: "string",
          example: "ABCD1234567",
        },
        {
          path: "containers[].quantity",
          label: "Aantal",
          group: "Containers",
          type: "number",
          example: 1,
        },
        {
          path: "containers[].goodsDescription",
          label: "Goederenomschrijving",
          group: "Containers",
          type: "string",
          example: "Container with goods",
        },
        {
          path: "containers[].gross",
          label: "Brutogewicht",
          group: "Containers",
          type: "number",
          example: 50,
        },
        {
          path: "containers[].net",
          label: "Nettogewicht",
          group: "Containers",
          type: "number",
          example: 50,
        },
        {
          path: "containers[].volume",
          label: "Volume",
          group: "Containers",
          type: "number",
          example: 50,
        },
        {
          path: "containers[].currency",
          label: "Valuta",
          group: "Containers",
          type: "string",
          example: "EUR",
        },
        {
          path: "containers[].commercialValue",
          label: "Commerciële waarde",
          group: "Containers",
          type: "number",
          example: 100,
        },
        {
          path: "containers[].orderNumber",
          label: "Ordernummer",
          group: "Containers",
          type: "string",
          example: "O001",
        },
        {
          path: "containers[].sealNumber",
          label: "Sealnummer",
          group: "Containers",
          type: "string",
          example: "SN01",
        },
        {
          path: "containers[].marksAndNumbers",
          label: "Marks & numbers",
          group: "Containers",
          type: "string",
          example: "MARK01",
        },
        {
          path: "containers[].hsCodes",
          label: "HS-codes",
          group: "Containers",
          type: "array",
          example: ["fake-code"],
        },
        {
          path: "containers[].dgEnabled",
          label: "Dangerous goods",
          group: "Containers",
          type: "boolean",
          example: true,
        },
        {
          path: "containers[].unCodes",
          label: "UN-codes",
          group: "Containers",
          type: "array",
          example: ["UN1006"],
        },
      ],
    },
    {
      path: "packages",
      label: "Packages",
      group: "Packages",
      type: "array",
      children: [
        {
          path: "packages[].packageType",
          label: "Package type",
          group: "Packages",
          type: "string",
          example: "BAG",
        },
        {
          path: "packages[].quantity",
          label: "Aantal",
          group: "Packages",
          type: "number",
          example: 1,
        },
        {
          path: "packages[].goodsDescription",
          label: "Goederenomschrijving",
          group: "Packages",
          type: "string",
        },
        {
          path: "packages[].lengthCm",
          label: "Lengte cm",
          group: "Packages",
          type: "number",
        },
        {
          path: "packages[].widthCm",
          label: "Breedte cm",
          group: "Packages",
          type: "number",
        },
        {
          path: "packages[].heightCm",
          label: "Hoogte cm",
          group: "Packages",
          type: "number",
        },
        {
          path: "packages[].volume",
          label: "Volume",
          group: "Packages",
          type: "number",
        },
        {
          path: "packages[].weight",
          label: "Gewicht",
          group: "Packages",
          type: "number",
        },
        {
          path: "packages[].net",
          label: "Nettogewicht",
          group: "Packages",
          type: "number",
        },
        {
          path: "packages[].stackable",
          label: "Stapelbaar",
          group: "Packages",
          type: "boolean",
        },
      ],
    },
  ],
};
